import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"
import { cn } from "@/lib/utils"

export type ToggleItem = {
  value: string
  text?: string
  icon?: React.ReactNode
  disabled?: boolean
}

export const ToggleGroup = ({
  className,
  invalid,
  ...props
}: React.ComponentPropsWithRef<typeof ToggleGroupPrimitive.Root> & { invalid?: boolean }) => (
  <ToggleGroupPrimitive.Root
    className={cn(
      "bg-surface flex min-h-12 items-center justify-center rounded-md",
      invalid ? "outline-critical!" : "outline-border!",
      className,
    )}
    style={{ outlineStyle: "solid", outlineWidth: 1 }}
    aria-invalid={invalid}
    {...props}
  />
)

type ToggleGroupItemProps = React.ComponentPropsWithRef<typeof ToggleGroupPrimitive.Item> & { "data-testid": string }
export const ToggleGroupItem = ({ className, id, ...props }: ToggleGroupItemProps) => (
  <ToggleGroupPrimitive.Item
    id={id || props["data-testid"]}
    className={cn(
      "text-on-surface-subdued inline-flex h-full w-full items-center justify-center rounded-md px-3 py-3.5 text-sm leading-5 outline-1 transition duration-150 outline-solid",
      "data-[state=on]:bg-primary-surface data-disabled:text-on-surface-disabled data-[state=on]:text-on-primary-surface data-[state=on]:outline-on-primary-surface data-[state=off]:hover:bg-surface-hovered data-disabled:hover:text-on-surface-disabled! data-[state=off]:hover:text-on-surface data-disabled:cursor-not-allowed data-disabled:hover:bg-transparent! data-[state=off]:outline-transparent",
      className,
    )}
    {...props}
  />
)

type CustomToggleGroupProps = {
  items: ToggleItem[]
  iconOnly?: boolean
  testId?: string
  invalid?: boolean
} & Omit<ToggleGroupPrimitive.ToggleGroupSingleProps, "type">

export const CustomToggleGroup = ({
  items,
  iconOnly,
  testId = "toggle-group",
  invalid,
  ...props
}: CustomToggleGroupProps) => {
  return (
    <ToggleGroup type="single" data-testid={testId} invalid={invalid} {...props}>
      {items.map(({ text = "", icon, ...item }, index) => (
        <ToggleGroupItem key={item.value} {...item} data-testid={`toggle-item-${index}`}>
          {iconOnly && icon ? icon : text}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}
